import { useCallback, useState, type ChangeEvent } from 'react';
import { useIntl } from 'react-intl';
import { useTravelCardIsClaimable } from '@/api/trafikportalen';
import {
  CARD_SERIAL_LENGTH,
  formatCardSerial,
  isCardSerialReady,
  normalizeCardSerialInput,
} from '@/helpers/cardSerial';
import { getClaimabilityError } from '@/helpers/claimability';

export const useClaimableCardSerial = () => {
  const { formatMessage } = useIntl();
  const [serial, setSerial] = useState('');

  const isComplete = serial.length === CARD_SERIAL_LENGTH;

  const {
    data: claimability,
    isFetching,
    isError,
  } = useTravelCardIsClaimable(
    { serial },
    {
      query: {
        enabled: isComplete,
        retry: false,
      },
    },
  );

  const isChecking = isComplete && isFetching && !claimability && !isError;

  const ready = isCardSerialReady({
    serial,
    isChecking,
    isError: isComplete && isError,
    claimable: claimability?.claimable,
  });

  let errorMessage = '';
  if (serial.length > 0 && !isComplete) {
    errorMessage = formatMessage({
      defaultMessage: 'Kortnumret måste vara 16 siffror',
    });
  } else if (isComplete && isError) {
    errorMessage = formatMessage({
      defaultMessage: 'Kunde inte kontrollera kortet',
    });
  } else if (isComplete && claimability && !claimability.claimable) {
    errorMessage = getClaimabilityError(claimability.reason, formatMessage);
  }

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSerial(normalizeCardSerialInput(event.target.value));
    },
    [],
  );

  return {
    serial,
    formattedSerial: formatCardSerial(serial),
    ready,
    isChecking,
    isClaimable: ready && isComplete,
    errorMessage,
    handleChange,
  };
};
